import { useMemo } from 'react'
import { DateTime } from 'luxon'
import { Attributes } from '@leverege/ui-attributes'
import { getBusinessDays } from '../../../../util'

function rollupTime( time, interval = 900000 ) {
  return Math.floor( time / interval ) * interval
}

function getParentLine( moldHalfId, data, sidesToShow ) {
  const lines = data?.items

  const parentLineArray = lines.find( ( line ) => {
    for ( let i = 0; i < sidesToShow.length; i++ ) {
      if ( moldHalfId === line?.data?.data[sidesToShow[i]]?.id ) {
        return true
      }
    }
    return false
  } )

  if ( parentLineArray == null || parentLineArray.length === 0 ) return null
  return parentLineArray
}

function getStage( event, cycleEvents ) {
  const ces = cycleEvents[event.deviceId]
  if ( ces == null ) return 'N/A'
  for ( let i = 0; i < ces.length; i++ ) {
    if ( !ces[i + 1] ) {
      return ces[i].data.data.stage ?? ces[i].data.data.status
    }
    if ( event.data.time > ces[i].data.time && event.data.time < ces[i + 1].data.time ) {
      
      return ces[i].data.data.stage ?? ces[i].data.data.status
    }
  }
  return 'N/A'
}

export default function useOccupancyGraphData( opts ) {
  const { parentItem, data, result, sidesToShow = [ 'uwMold', 'lwMold' ], timeRange } = opts

  const operatingHours = parentItem.data?.metadata?.configuration?.operatingHours?.default
  
  const graphData = useMemo( () => {
    const cycleEvents = result.items?.filter( ( event ) => {
      return event?.type === 'cycleTracking'
    } ).reduce( ( acc, cur ) => {
      if ( !acc[cur.deviceId] ) {
        acc[cur.deviceId] = []
      }
      acc[cur.deviceId].push( cur )
      return acc 
    }, {} )

    const bd = getBusinessDays( {
      start : new Date( result.start ),
      end : new Date( result.end ),
    }, operatingHours, { getFullRange : true } )
    let s, e
    if ( bd.length > 0 ) {
      s = new Date( bd[0][0] ).toISOString()
      e = new Date( bd.at( -1 )[1] ).toISOString()
    }
    
    const ret = result?.items?.reduce( ( acc, cur ) => {

      if ( cur?.type === 'occupancy' ) {
        const { data : itemData, data : { time }, deviceId } = cur
        const { resources } = itemData
        if ( time > s && time < e ) {

          const hour = new Date( rollupTime( new Date( time ).getTime() ) ).toISOString()
          
          const parentLine = getParentLine( deviceId, data, sidesToShow )
          if ( parentLine ) {
            if ( !acc[hour] ) {
              acc[hour] = {
                halves : {
                  [deviceId] : {
                    parentLineId : parentLine.id
                  }
                }
              }
            } else {
              acc[hour].halves[deviceId] = { parentLineId : parentLine.id }
            }
            const datum = acc[hour].halves[deviceId]
            datum.count = itemData.data.counts?.person
            datum.countByCategory = itemData.data.counts?.byCategory
            /*
              TODO: add category data
              data/counts/byCategory/:category
                - direct
                - indirect
                - indirectOther
                - uncategorized
            */
            if ( !acc[`${hour}`].halves[deviceId].imgSrc && resources?.[0]?.path ) {
              acc[`${hour}`].halves[deviceId].imgSrc = resources[0].path
              acc[`${hour}`].halves[deviceId].imgDeviceId = deviceId // resources[0].device.deviceId
              acc[`${hour}`].halves[deviceId].stage = getStage( cur, cycleEvents )
            }
          }
        }
      }
      return acc
    }, {} ) || {}

    const d = Object.entries( ret ).map( ( [ hour, data ] ) => {
      return {
        hour,
        halves : data.halves
      }
    } )

    if ( d.length === 0 ) {
      return d
    }

    const res = getBusinessDays( {
      start : new Date( result.start ),
      end : new Date( result.end ),
    }, operatingHours )
    const start = res?.length > 0 ?
      DateTime.fromMillis( res[0][0] ).minus( { hour : 1 } ).toJSDate() :
      DateTime.now().startOf( 'day' ).toJSDate()
    const end = res?.length > 0 ?
      DateTime.fromMillis( res.at( -1 )[1] ).plus( { hour : 1 } ).toJSDate() :
      DateTime.now().endOf( 'day' ).toJSDate()

    if ( start < new Date( d[0].hour ) ) {
      d.unshift( { hour : start.toISOString() } )
    }

    if ( end > new Date( d.at( -1 ).hour ) ) {
      const strippedHalves = { ...d.at( -1 )?.halves }
      Object.keys( strippedHalves ).forEach( ( key ) => {
        strippedHalves[key] = { ...strippedHalves[key] }
        delete strippedHalves[key].imgSrc
      } )
      d.push( {
        hour : DateTime.fromMillis( res.at( -1 )[1] ).toJSDate().toISOString(),
        halves : strippedHalves
      } )
    }
    
    return d.filter( entry => ( new Date( entry.hour ) ).getTime() > timeRange.start.getTime() )
  }, [ operatingHours, result, sidesToShow, data, timeRange.start ] )

  const config = useMemo( () => {
    const xAccessor = ( d ) => {
      return d ? new Date( d.hour ).valueOf() : 0
    }

    const res = getBusinessDays( {
      start : new Date( result.start ),
      end : new Date( result.end ),
    }, operatingHours, { getFullRange : true } )

    const start = res?.length > 0 ?
      Math.max( res[0][0], timeRange.start.getTime() ) :
      DateTime.now().startOf( 'day' ).valueOf()

    return {
      scale : {
        x : {
          type : 'time',
          domain : [
            DateTime.fromMillis( start ).minus( { hour : 1 } ).valueOf(),
            DateTime.fromMillis( new Date( result.end ).getTime() ).plus( { hour : 1 } ).valueOf()
          ]
        },
        y : { type : 'linear' },
      },
      accessors : {
        halves : data.items.reduce( ( prev, item ) => {
          const uwId = Attributes.get( 'mold.uwMold', item )
          const lwId = Attributes.get( 'mold.lwMold', item )

          if ( uwId ) {
            prev[uwId] = {
              xAccessor,
              yAccessor : ( d ) => {
                return d?.halves?.[uwId]?.count || 0
              }
            }
          }

          if ( lwId ) {
            prev[lwId] = {
              xAccessor,
              yAccessor : ( d ) => {
                return d?.halves?.[lwId]?.count || 0
              }
            }
          }

          return prev
        }, {} )
      },
      tickFormat : {
        x : ( value ) => {
          const date = DateTime.fromJSDate( value )
          const hour = date.get( 'hour' )
          const day = date.get( 'day' )
          const month = date.get( 'month' )

          if ( hour === 0 ) {
            return `${month}/${day}`// 12AM`
          }
          if ( hour === 12 ) {
            return `${hour} PM`
          }
          if ( hour < 12 ) {
            return `${hour} AM`
          }

          return `${hour - 12} PM`
        },
        y : value => parseFloat( value )
        
      },
    }
  }, [ data.items, operatingHours, result, timeRange.start ] )

  return {
    data : graphData,
    config
  }
}
